export interface IConfigurationError {
    get message(): string;
    get trace(): string;
}

export class ConfigurationError implements IConfigurationError {
    readonly #msg: string;
    readonly #trace: string | undefined;

    constructor( message: string ) {
        this.#msg = message;
        this.#trace = new Error().stack;
    }

    get message(): string {
        return this.#msg;
    }

    get trace(): string {
        return this.#trace ?? '';
    }
}

export interface INetworkError {
    get code(): number;
    get message(): string;
}

abstract class NetworkError implements INetworkError {
    readonly #code: number;
    readonly #msg: string;

    protected constructor( code: number, msg: string ) {
        this.#code = code;
        this.#msg = msg;
    }

    get code(): number {
        return this.#code;
    }

    get message(): string {
        return this.#msg;
    }
}

export class ApiError extends NetworkError {
    constructor( code: number, message: string ) {
        super( code, message );
    }
}

export class AuthenticationFailed extends NetworkError {
    private static readonly _failedAuth: number = -1;

    static get statusCode(): number {
        return AuthenticationFailed._failedAuth;
    }

    constructor( msg: string ) {
        super( AuthenticationFailed._failedAuth, msg );
    }
}

export class Unauthorized extends NetworkError {
    private static readonly _unauthorized: number = 401;

    static get statusCode(): number {
        return Unauthorized._unauthorized;
    }

    constructor() {
        super( Unauthorized._unauthorized, 'UNAUTHORIZED' );
    }
}

export class Forbidden extends NetworkError {
    static readonly _forbidden: number = 403;

    static get statusCode(): number {
        return Forbidden._forbidden;
    }

    constructor() {
        super( Forbidden._forbidden, 'FORBIDDEN' );
    }
}

export class InternalError extends NetworkError {
    static readonly _internalError: number = 500;

    static get statusCode(): number {
        return InternalError._internalError;
    }

    constructor() {
        super( InternalError._internalError, 'INTERNAL ERROR' );
    }
}

export class BadRequest extends NetworkError {
    static readonly _badRequest: number = 400;

    static get statusCode(): number {
        return BadRequest._badRequest;
    }

    constructor( msg: string | null | undefined ) {
        super( BadRequest._badRequest, msg ?? 'BAD REQUEST' );
    }
}

export class NotFound extends NetworkError {
    static readonly _resourceNotFound: number = 404;

    static get statusCode(): number {
        return NotFound._resourceNotFound;
    }

    constructor() {
        super( NotFound._resourceNotFound, 'NOT FOUND' );
    }
}

export class Unknown extends NetworkError {
    static readonly _unknown: number = 999;

    static get statusCode(): number {
        return Unknown._unknown;
    }

    constructor( trace: string ) {
        super( Unknown._unknown, trace );
    }
}

export class MissingData extends Error {
    constructor( ...params: any[] ) {
        // Pass remaining arguments (including vendor specific ones) to parent constructor
        super( ...params );

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if ( Error.captureStackTrace ) {
            Error.captureStackTrace( this, MissingData );
        }

        this.name = "MissingData";
    }
}
